import {
  LatencyClimbGate,
  RECOVERY_HEALTHY_HOLD_MS,
  RECOVERY_IDLE_BEFORE_CLIMB_MS,
  RECOVERY_KEYFRAME_ONCE,
  TARGET_FRAMERATE,
  isPlayerInteractingSinceBaseline,
  shouldRequestIdr,
} from '@playcastdotio/PeerCore';
import type { DecodeLagEpisode, LatencyClimbGateSample, SessionDowngradeFloor } from '@playcastdotio/PeerCore';
import {
  CLIMB_RAMP_STEPS,
  climbCeilingFor,
  exitQueueThresholdFor,
  rampBitrateFor,
  type RecoveryCutContext,
} from './RecoveryCutPolicy';
import type { RecoveryEpisodeStore } from './RecoveryEpisodeStore';

export type RecoveryClimbDeps = {
  episodes: RecoveryEpisodeStore;
  /** Climb-episode IDR only — never called from recovery enter. */
  requestKeyframeOnce: (peerId: string) => void;
  cutContextFor: (peerId: string) => RecoveryCutContext;
  /** The peer's sticky floor ('none' without a policy). */
  stickyFloorFor: (peerId: string) => SessionDowngradeFloor;
  /** True while a sticky floor holds the episode open at its ceiling (D1). */
  floorBlocksClimb: (peerId: string) => boolean;
  /** The guest's active decode-lag episode, or null: the climb IDR is withheld while it lasts. */
  decodeLagEpisodeFor: (peerId: string) => DecodeLagEpisode | null;
  nowMs: () => number;
  healthyHoldMs?: (peerId: string) => number | undefined;
};

type PeerActivity = {
  lastActivityAtMs: number | null;
  hasEverReceivedActivity: boolean;
  /**
   * When the host started watching this peer for activity. Silence measured from here is
   * proof of idle; without it, silence is only missing evidence.
   */
  activityBaselineAtMs: number | null;
};

/**
 * RecoveryClimbManager — the way back out of a recovery episode.
 *
 * Owns the exit ramp (the healthy hold, the three-step climb to the ceiling, the sticky-floor
 * hold), the latency/FPS climb gate (PRODUCT DECISION B) and the player-activity record that
 * decides when the one climb IDR may fire. The climb runs during active play: a
 * `setParameters` bitrate change does not force a keyframe and does not hitch, so gating it
 * on idleness only pinned a recovered stream at the low rung for the rest of the session. The
 * IDR *does* hitch, so it alone waits for an idle tick.
 */
export class RecoveryClimbManager {
  private readonly activityByPeer = new Map<string, PeerActivity>();
  private readonly climbGateByPeer = new Map<string, LatencyClimbGate>();

  constructor(private readonly deps: RecoveryClimbDeps) {}

  /**
   * Marks the point from which this peer's silence counts as idleness. The first call per
   * connection wins so repeated re-primes cannot push the baseline forward and starve the climb.
   */
  public beginActivityWatch(peerId: string): void {
    const existing = this.activityByPeer.get(peerId);
    if (existing?.activityBaselineAtMs != null) return;
    this.activityByPeer.set(peerId, {
      lastActivityAtMs: existing?.lastActivityAtMs ?? null,
      hasEverReceivedActivity: existing?.hasEverReceivedActivity ?? false,
      activityBaselineAtMs: this.deps.nowMs(),
    });
  }

  /**
   * Records activity only. It deliberately does not touch `healthySince`, `rampStep` or
   * `cutFloorBitrateBps`: resetting the climb on every input event is what pinned the
   * stream low, because the climb then needed five idle seconds a player never gives it.
   */
  public onPlayerActivity(peerId: string): void {
    const now = this.deps.nowMs();
    this.activityByPeer.set(peerId, {
      lastActivityAtMs: now,
      hasEverReceivedActivity: true,
      activityBaselineAtMs: this.activityByPeer.get(peerId)?.activityBaselineAtMs ?? now,
    });
  }

  /** Feed the climb gate one guest sample (PRODUCT DECISION B). */
  public observeGate(peerId: string, sample: LatencyClimbGateSample, ladderHealthy: boolean): void {
    this.climbGateFor(peerId).observe(sample, ladderHealthy);
  }

  public isGateLocked(peerId: string): boolean {
    return this.climbGateByPeer.get(peerId)?.isLocked() ?? false;
  }

  public invalidateEvidence(peerId: string): void {
    this.climbGateFor(peerId).invalidateEvidence();
  }

  public invalidateTransportEvidence(peerId: string): void {
    const episode = this.deps.episodes.get(peerId);
    if (episode) episode.healthySince = null;
  }

  /**
   * One healthy-or-not tick against the episode's own exit threshold. Advances the ramp when
   * the queue has drained past the healthy hold; clears `healthySince` when it has not.
   */
  public considerExit(peerId: string, standingQueueMs: number): void {
    const peer = this.deps.episodes.get(peerId);
    if (!peer || peer.state === 'normal') return;
    const exitQueueThresholdMs = exitQueueThresholdFor(peer.episodeCutKind);
    const now = this.deps.nowMs();
    if (!Number.isFinite(standingQueueMs) || standingQueueMs < 0 || standingQueueMs > exitQueueThresholdMs) {
      peer.healthySince = null;
      return;
    }
    // A locked latency/FPS gate parks the climb even when the queue has drained: bitrate does
    // NOT increase while the player is seeing fixable latency over budget or <30fps. (A
    // distant guest's floor never locks it — distance is not fault.)
    if (this.climbGateFor(peerId).isLocked()) {
      peer.healthySince = null;
      return;
    }
    if (peer.healthySince === null) {
      peer.healthySince = now;
      // Restart the escalate clock while holding healthy so a re-critical cannot use a stale
      // enteredAt from before the network recovered.
      peer.enteredAt = now;
    }
    if (now - peer.healthySince < (this.deps.healthyHoldMs?.(peerId) ?? RECOVERY_HEALTHY_HOLD_MS)) return;
    this.rampStep(peerId, standingQueueMs <= exitQueueThresholdMs, now);
  }

  /**
   * A sticky floor was released: rebase the open episode so the ramp climbs from the cap it
   * has now toward the new ceiling in the usual steps (never a one-tick jump), and re-prime
   * the encoding with the restored fps ceiling. Returns false when no episode is open.
   */
  public rebaseAfterRelease(peerId: string): boolean {
    const peer = this.deps.episodes.get(peerId);
    if (!peer || peer.state === 'normal') return false;
    const now = this.deps.nowMs();
    // Health at the old cap permits a measured ramp, not an immediate ceiling jump.
    const ceiling = climbCeilingFor(this.deps.stickyFloorFor(peerId), this.deps.cutContextFor(peerId));
    peer.maxBitrateBps = Math.min(peer.maxBitrateBps, ceiling);
    peer.cutFloorBitrateBps = peer.maxBitrateBps;
    peer.rampStep = 0;
    peer.healthySince = now;
    peer.enteredAt = now;
    peer.state = 'recovering';
    // peer.maxFramerate = this.deps.cutContextFor(peerId).fpsCeiling;
    peer.maxFramerate = TARGET_FRAMERATE;
    this.deps.episodes.apply(peerId, peer);
    return true;
  }

  public clearPeer(peerId: string): void {
    this.activityByPeer.delete(peerId);
    this.climbGateByPeer.delete(peerId);
  }

  public clearAll(): void {
    this.activityByPeer.clear();
    this.climbGateByPeer.clear();
  }

  private rampStep(peerId: string, queueDrained: boolean, now: number): void {
    const peer = this.deps.episodes.get(peerId);
    if (!peer) return;
    const ctx = this.deps.cutContextFor(peerId);
    const climbCeiling = climbCeilingFor(this.deps.stickyFloorFor(peerId), ctx);
    const floor = Math.min(peer.cutFloorBitrateBps, climbCeiling);

    // The bitrate ramp below is unconditional; only this keyframe waits for quiet. Never
    // while the guest reports an active decode-lag episode (a keyframe landing on a backlog
    // hitches the stream); once the queue has drained and the healthy hold has completed.
    if (
      RECOVERY_KEYFRAME_ONCE &&
      !peer.keyframeSent &&
      !this.isInteracting(peerId) &&
      shouldRequestIdr(this.deps.decodeLagEpisodeFor(peerId), queueDrained, /* holdComplete */ true)
    ) {
      peer.keyframeSent = true;
      this.deps.requestKeyframeOnce(peerId);
    }

    peer.rampStep += 1;
    peer.maxBitrateBps = rampBitrateFor(floor, climbCeiling, peer.rampStep);
    // The climb used to keep the sticky fps ceiling (no forced 60). The sender stays at 60.
    // peer.maxFramerate = ctx.fpsCeiling;
    peer.maxFramerate = TARGET_FRAMERATE;
    console.log('[CongestionRecovery] Within-tier climb', {
      peerId,
      episodeCut: peer.episodeCutKind,
      step: `${peer.rampStep}/${CLIMB_RAMP_STEPS}`,
      fromBitrateBps: floor,
      maxBitrateBps: peer.maxBitrateBps,
      maxFramerate: peer.maxFramerate,
      idrRequestedThisEpisode: peer.keyframeSent,
    });
    this.deps.episodes.apply(peerId, peer);

    if (peer.rampStep < CLIMB_RAMP_STEPS) {
      peer.state = 'recovering';
      peer.healthySince = now;
      peer.enteredAt = now;
      return;
    }
    if (this.deps.floorBlocksClimb(peerId)) {
      // PRODUCT DECISION D1: the floor holds. The episode stays open at the floor's ceiling
      // (state stays 'recovering' so applyEncodingRespectingRecovery keeps the override)
      // until the reclaim manager releases the floor on measured evidence.
      peer.maxBitrateBps = climbCeiling;
      // peer.maxFramerate = ctx.fpsCeiling;
      peer.maxFramerate = TARGET_FRAMERATE;
      peer.state = 'recovering';
      peer.healthySince = now;
      peer.enteredAt = now;
      this.deps.episodes.apply(peerId, peer);
      return;
    }
    this.deps.episodes.end(peerId, {
      recoveryCleared: true,
      clearedAtMs: now,
      lastRecoveryMaxBitrateBps: floor,
      lastIdrRequested: peer.keyframeSent,
    });
  }

  private climbGateFor(peerId: string): LatencyClimbGate {
    let gate = this.climbGateByPeer.get(peerId);
    if (!gate) {
      gate = new LatencyClimbGate({ nowMs: this.deps.nowMs });
      this.climbGateByPeer.set(peerId, gate);
    }
    return gate;
  }

  private isInteracting(peerId: string): boolean {
    const activity = this.activityByPeer.get(peerId);
    return isPlayerInteractingSinceBaseline(
      activity?.lastActivityAtMs ?? null,
      activity?.activityBaselineAtMs ?? null,
      this.deps.nowMs(),
      activity?.hasEverReceivedActivity ?? false,
      RECOVERY_IDLE_BEFORE_CLIMB_MS,
    );
  }
}
